'use client';

import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { CheckCircle2, Circle } from 'lucide-react';
import {
  markModuleCompleted,
  getModuleProgress,
  type ModuleProgress,
} from '@/lib/storage/moduleProgress';

interface ModuleCompletionButtonProps {
  moduleId: string;
}

/**
 * Client component for marking modules as complete
 */
export function ModuleCompletionButton({ moduleId }: ModuleCompletionButtonProps) {
  const [progress, setProgress] = useState<ModuleProgress | null>(null);
  const [isCompleting, setIsCompleting] = useState(false);

  useEffect(() => {
    getModuleProgress(moduleId).then(setProgress);
  }, [moduleId]);

  const handleComplete = async () => {
    setIsCompleting(true);
    try {
      await markModuleCompleted(moduleId);
      const updated = await getModuleProgress(moduleId);
      setProgress(updated);
    } catch (error) {
      console.error('Failed to mark module as complete:', error);
    } finally {
      setIsCompleting(false);
    }
  };

  if (progress?.completed) {
    return (
      <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400">
        <CheckCircle2 className="h-5 w-5" />
        <span className="text-sm font-medium">Completed</span>
      </div>
    );
  }

  return (
    <Button
      onClick={handleComplete}
      disabled={isCompleting}
      variant="outline"
      className="flex items-center gap-2"
    >
      <Circle className="h-4 w-4" />
      {isCompleting ? 'Marking...' : 'Mark as Complete'}
    </Button>
  );
}
